import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { convertToPercent } from '@openx/utils/lib/convertToPercent';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartnerNames } from '../../../utils/mapDataPartnerNames';
import type { AudiencePricingResult } from '../../sections/MultiPartnerPricing';

type PartnerPricingAlertProps = {
  prices: AudiencePricingResult['computePricing']['pricing_by_org'];
  partnerNames: PartnerNames;
};

export const PartnerPricingAlert = ({ prices, partnerNames }: PartnerPricingAlertProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  const organizationIds = useMemo(() => Object.keys(prices), [prices]);

  return (
    <Alert
      data-test="partner-pricing-alert"
      severity="info"
      sx={{
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          height: 'fit-content',
          gap: 3,
        }}
      >
        <Typography data-test="partner-pricing-title" variant="body1Bold" component="span">
          {t('Data Partner Pricing')}
        </Typography>
        <Button
          onClick={() => setShow(prev => !prev)}
          aria-expanded={show}
          variant="text"
          color="primary"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {show ? t('Hide details') : t('Show details')}
        </Button>
      </Box>

      <Collapse data-test="partner-pricing-info" in={show} timeout="auto" unmountOnExit>
        {t("You've selected segments from 3P Data Partners - pricing details are outlined below.")}

        {organizationIds.map(orgId => (
          <Box
            key={orgId}
            data-test={`partner-pricing-org-${orgId}`}
            sx={{
              mt: 1.5,
            }}
          >
            <Typography data-test="partner-pricing-organization-name" variant="body1Bold" component="span">
              {partnerNames[orgId] ?? orgId}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <Typography
                data-test="info-on-alert-percent-of-media"
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('Percent of Media:')}
              </Typography>

              <Typography data-test="info-on-alert-percent-of-media-value" variant="body2">
                {convertToPercent(prices[orgId].price_pom ?? 0)}%
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <Typography
                data-test="info-on-alert-fixed-cpm"
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('Fixed CPM:')}
              </Typography>
              <Typography data-test="info-on-alert-fixed-cpm-value" variant="body2">
                $
                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
                  prices[orgId].price_cpm
                )}
              </Typography>
            </Box>
          </Box>
        ))}
      </Collapse>
    </Alert>
  );
};
